<template>
    <div class="print-settings">
        <div>
            <h1>{{ $t('settings.title') }}</h1>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.organizationTab.title') }}</h2>
            <table class="table">
                <tbody>
                    <tr>
                        <td class="no-wrap">{{ $t('settings.organizationTab.name') }}</td>
                        <td>{{ organizationName }}</td>
                    </tr>
                    <tr>
                        <td class="no-wrap">{{ $t('settings.organizationTab.information') }}</td>
                        <td>{{ contactInfo }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.assignmentsConfTab.title') }}</h2>
            <div>
                <h4 class="section-title">{{ $t('settings.assignmentsConfTab.callCategories') }}</h4>
            </div>
            <table class="table">
                <tr>
                    <th></th>
                    <th>{{ $t('reports.show') }}</th>
                    <th>{{ $t('print.nameOfCategory') }}</th>
                    <th>{{ $t('settings.assignmentsConfTab.color') }}</th>
                </tr>
                <tbody>
                    <tr>
                        <td>{{ $t('print.residentCallCategory') }}</td>
                        <td>
                            <i v-if="assignmentsConf.show_resident_call" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                        <td>{{ assignmentsConf.resident_call_name }}</td>
                        <td>
                            <span
                                :class="
                                    'color-' +
                                        getOption('assignmentsColors', assignmentsConf.resident_color).color
                                "
                            >
                                {{ getOption('assignmentsColors', assignmentsConf.resident_color).name }}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('print.staffCallCategory') }}</td>
                        <td>
                            <i v-if="assignmentsConf.show_staff_call" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                        <td>{{ assignmentsConf.staff_call_name }}</td>
                        <td>
                            <span
                                :class="
                                    'color-' +
                                        getOption('assignmentsColors', assignmentsConf.staff_color).color
                                "
                            >
                                {{ getOption('assignmentsColors', assignmentsConf.staff_color).name }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table class="table">
                <tbody>
                    <tr>
                        <h4 class="section-title">{{ $t('settings.assignmentsConfTab.typeAndLevel') }}</h4>
                    </tr>
                    <tr>
                        <td>{{ $t('settings.assignmentsConfTab.assignmentsType') }}</td>
                        <td>
                            {{ getOption('typeOptions', assignmentsConf.assignments_type).name }}
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('settings.assignmentsConfTab.assignmentsLevel') }}</td>
                        <td>
                            {{ getOption('levelOptions', assignmentsConf.assignments_level).name }}
                        </td>
                    </tr>
                    <tr>
                        <h4 class="section-title">
                            {{ $t('settings.assignmentsConfTab.catchNetVisibility') }}
                        </h4>
                    </tr>
                    <tr>
                        <td>{{ $t('settings.assignmentsConfTab.catchNetVisibility') }}</td>
                        <td>
                            {{ assignmentsConf.show_catch_net ? 'visible' : 'hidden' }}
                        </td>
                    </tr>
                    <tr>
                        <h4 class="section-title">
                            {{ $t('settings.assignmentsConfTab.staffOverloadSetting') }}
                        </h4>
                    </tr>
                    <tr>
                        <td>{{ $t('settings.assignmentsConfTab.staffOverloadLabel') }}</td>
                        <td>{{ assignmentsConf.staff_overload }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.targetTimeTab.title') }}</h2>
            <table class="table target-time-table">
                <tr>
                    <th>{{ $t('settings.targetTimeTab.callType') }}</th>
                    <th>{{ $t('settings.targetTimeTab.targetTimeEnable') }}</th>
                    <th>{{ $t('settings.targetTimeTab.targetTime') }} (mm:ss)</th>
                    <th>{{ $t('settings.targetTimeTab.delayedTime') }} (mm:ss)</th>
                    <th>{{ $t('settings.targetTimeTab.targetTimeTill') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(call, name) in calls" :key="call.category_id">
                        <td>
                            <i :class="'icon-' + iconName(name)"></i>{{ $t('reporting.callTypes.' + name) }}
                        </td>
                        <td>
                            <i v-if="call.time_target_enabled" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                        <td>
                            {{ call.time_target_enabled ? secondsToMMSS(call.time_target) : '' }}
                        </td>
                        <td>
                            {{
                                call.time_target_enabled
                                    ? secondsToMMSS(call.time_delayed - call.time_target)
                                    : ''
                            }}
                        </td>
                        <td>{{ call.time_target_enabled ? findTillType(call.time_till) : '' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.logSettingsTab.title') }}</h2>
            <table class="table">
                <tbody>
                    <tr v-for="setting in settings" :key="setting.id">
                        <td class="no-wrap">{{ setting.name }}</td>
                        <td>
                            <i v-if="setting.value" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.tabVisibilityTab.title') }}</h2>
            <table class="table">
                <tbody>
                    <tr v-for="(tab, name) in tabVisibility" :key="tab.id">
                        <td class="no-wrap">
                            {{ $t('settings.tabVisibilityTab.' + name) }}
                            <span v-if="tab.enabled === false" class="enabled-function">{{
                                '(' + $t('settings.tabVisibilityTab.disabled_function') + ')'
                            }}</span>
                        </td>
                        <td>
                            <i v-if="tab.visible" class="icon-checkmark"></i>
                            <i v-else class="icon-close"></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="setting-tab">
            <h2>{{ $t('settings.otherTab.title') }}</h2>
            <table class="table">
                <tbody>
                    <tr>
                        <td class="no-wrap">{{ $t('settings.autoLogoutMinutes.name') }}</td>
                        <td>{{ autoLogout }}</td>
                    </tr>
                    <tr>
                        <td class="no-wrap">{{ $t('settings.otherTab.timeFormat') }}</td>
                        <td>{{ formatedTime }}</td>
                    </tr>
                    <tr>
                        <td class="no-wrap">{{ $t('settings.otherTab.default_recipients') }}</td>
                        <td class="wrap-text" v-html="defaultEmails"></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapState } from 'vuex';
import { getCancelToken } from '../../api';
import { secondsToMMSS } from '../common/helpers';
import format from 'date-fns/format';

export default {
    name: 'SettingsPrintVersion',

    data() {
        return {
            assignments: {},
            typeOptions: [
                {
                    value: 0,
                    name: this.$t('settings.assignmentsConfTab.typeOptions.directAndDuty'),
                },
                {
                    value: 1,
                    name: this.$t('settings.assignmentsConfTab.typeOptions.direct'),
                },
                {
                    value: 2,
                    name: this.$t('settings.assignmentsConfTab.typeOptions.duty'),
                },
            ],
            levelOptions: [
                {
                    value: 0,
                    name: this.$t('settings.assignmentsConfTab.levelOptions.one'),
                },
                {
                    value: 1,
                    name: this.$t('settings.assignmentsConfTab.levelOptions.two'),
                },
            ],
            assignmentsColors: [
                {
                    name: this.$t('settings.assignmentsConfTab.orange'),
                    value: 1,
                    color: 'orange',
                },
                {
                    name: this.$t('settings.assignmentsConfTab.red'),
                    value: 2,
                    color: 'red',
                },
                {
                    name: this.$t('settings.assignmentsConfTab.blue'),
                    value: 3,
                    color: 'blue',
                },
                {
                    name: this.$t('settings.assignmentsConfTab.green'),
                    value: 4,
                    color: 'green',
                },
            ],
            targetTime: [],
            tillTypes: [
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.callAccepted'),
                    value: 1,
                    hiddenInCategories: [3],
                },
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.nursePresence'),
                    value: 2,
                    hiddenInCategories: [4, 5, 6, 7, 8, 9, 10, 11],
                },
                {
                    name: this.$t('settings.targetTimeTab.tillTypes.callEnd'),
                    value: 3,
                    hiddenInCategories: [],
                },
            ],
            cancelToken: getCancelToken(),
            logSettings: [],
        };
    },

    computed: {
        ...mapState({
            assignmentsConf: state => state.assignmentsConf,
            autoLogout: state => state.mariTimeout,
            timeFormat: state => state.timeFormat.date + state.timeFormat.separator + state.timeFormat.time,
            contactInfo: state => state.navigation.contactInfo,
            organizationName: state => state.navigation.organizationName,
            tabVisibility: state => state.user.tabVisibility,
            calls: state => state.targetTimeCalls,
            settings: state => state.logSettings,
            defaultRecipients: state => state.defaultRecipients,
            mariCurrentTime: state => state.mariCurrentTime,
        }),

        formatedTime() {
            return format(this.mariCurrentTime, this.timeFormat);
        },

        defaultEmails() {
            let emails = '';
            this.defaultRecipients.forEach(element => {
                emails = emails + '<p>' + element + '</p>';
            });
            return emails;
        },
    },

    created() {
        this.defaultRecipients.forEach(element => {
            this.emails = this.emails + '<p>' + element + '</p>';
        });
    },

    methods: {
        secondsToMMSS,
        iconName(name) {
            return this.$t('reporting.callTypes.' + name).toLowerCase();
        },

        findTillType(value) {
            return this.tillTypes.find(type => type.value === value).name;
        },

        getOption(optionName, value) {
            return this[optionName].find(v => v.value === value) || [];
        },
    },
};
</script>
